/**
 * ASCII Art Generator - 特效系统类型定义
 */

import type { EffectDirection } from '../types'

// ============================================
// 特效类型枚举
// ============================================

/** 特效类型 */
export type EffectType =
  | 'vignette'   // 晕影
  | 'noise'      // 噪点
  | 'beam'       // 光束
  | 'intervals'  // 间隔条纹
  | 'waves'      // 波浪叠加
  | 'matrix'     // 数字雨
  | 'glitch'     // 故障
  | 'crt'        // CRT扫描线

/** 所有特效列表 */
export const EFFECT_LIST: EffectType[] = [
  'vignette', 'noise', 'beam', 'intervals',
  'waves', 'matrix', 'glitch', 'crt'
]

/** 特效显示名称 */
export const EFFECT_LABELS: Record<EffectType, string> = {
  vignette: 'Vignette 晕影',
  noise: 'Noise 噪点',
  beam: 'Beam 光束',
  intervals: 'Intervals 条纹',
  waves: 'Waves 波浪',
  matrix: 'Matrix 数字雨',
  glitch: 'Glitch 故障',
  crt: 'CRT 扫描线'
}

// ============================================
// 特效配置接口
// ============================================

/** Vignette 晕影配置 */
export interface VignetteConfig {
  strength: number    // 0.0-1.0 强度
  radius: number      // 0.3-1.0 范围
  softness: number    // 0.1-0.5 柔和度
}

/** Noise 噪点配置 (Film Grain) */
export interface NoiseConfig {
  strength: number    // 0.0-1.0 强度
  scale: number       // 1-10 颗粒大小
  density: number     // 0.1-1.0 密度
  colored: boolean    // 彩色噪点
  time: number        // 用于动画的时间
}

/** Beam 光束配置 */
export interface BeamConfig {
  strength: number    // 0.0-1.0 亮度
  width: number       // 10-100 宽度
  speed: number       // 0.1-10 速度
  direction: EffectDirection
  color: string       // 颜色
  time: number        // 时间
}

/** Intervals 间隔条纹配置 */
export interface IntervalsConfig {
  spacing1: number    // 5-50 第一条纹间距
  width1: number      // 1-10 第一条纹宽度
  spacing2: number    // 5-50 第二条纹间距
  width2: number      // 1-10 第二条纹宽度
  direction: EffectDirection
  animated: boolean   // 是否动画
  time: number        // 时间
}

/** Waves 波浪叠加配置 */
export interface WavesConfig {
  strength: number    // 0.0-1.0 强度
  frequency: number   // 0.01-0.2 频率
  speed: number       // 0.1-5 速度
  hueShift: number    // 0-360 色相偏移
  waveCount: number   // 2-5 波源数量
  time: number        // 时间
}

/** Matrix 数字雨配置 */
export interface MatrixConfig {
  strength: number    // 0.0-1.0 亮度
  speed: number       // 0.5-5 下落速度
  charSize: number    // 10-30 字符大小
  trailLength: number // 5-30 拖尾长度
  density: number     // 0.1-1.0 列密度
  mode: 'overlay' | 'replace'  // 模式
  time: number        // 时间
}

/** Glitch 故障配置 */
export interface GlitchConfig {
  rgbShift: number    // 0-20 RGB色散像素
  blockShift: number  // 0-50 块位移像素
  blockCount: number  // 0-10 随机块数量
  speed: number       // 0.1-5 变化频率
  direction: EffectDirection
  time: number        // 时间
}

/** CRT 扫描线配置 */
export interface CRTConfig {
  scanlineStrength: number  // 0.0-1.0 扫描线强度
  scanlineSpacing: number   // 2-4 扫描线间距
  curvature: number         // 0.0-0.5 屏幕弯曲
  edgeRgbShift: number      // 0-10 边缘色散
  crtVignette: number       // 0.0-1.0 CRT晕影
  time: number              // 时间
}

// ============================================
// 联合配置类型
// ============================================

/** 特效配置联合类型 */
export type EffectConfig =
  | VignetteConfig
  | NoiseConfig
  | BeamConfig
  | IntervalsConfig
  | WavesConfig
  | MatrixConfig
  | GlitchConfig
  | CRTConfig

// ============================================
// 特效状态接口
// ============================================

/** 特效启用状态 */
export interface EffectsState {
  vignette: { enabled: boolean; config: VignetteConfig }
  noise: { enabled: boolean; config: NoiseConfig }
  beam: { enabled: boolean; config: BeamConfig }
  intervals: { enabled: boolean; config: IntervalsConfig }
  waves: { enabled: boolean; config: WavesConfig }
  matrix: { enabled: boolean; config: MatrixConfig }
  glitch: { enabled: boolean; config: GlitchConfig }
  crt: { enabled: boolean; config: CRTConfig }
  /** 特效叠加顺序（用户可调整） */
  order: EffectType[]
}

/** 默认特效状态 - 参数已调整使效果更明显 */
export const DEFAULT_EFFECTS_STATE: EffectsState = {
  vignette: { enabled: false, config: { strength: 0.6, radius: 0.65, softness: 0.35 } },
  noise: { enabled: false, config: { strength: 0.5, scale: 2, density: 0.7, colored: false, time: 0 } },
  beam: { enabled: false, config: { strength: 0.7, width: 50, speed: 1.5, direction: 'right', color: '#ffffff', time: 0 } },
  intervals: { enabled: false, config: { spacing1: 15, width1: 4, spacing2: 25, width2: 3, direction: 'down', animated: false, time: 0 } },
  waves: { enabled: false, config: { strength: 0.6, frequency: 0.08, speed: 1.5, hueShift: 60, waveCount: 3, time: 0 } },
  matrix: { enabled: false, config: { strength: 0.7, speed: 1.5, charSize: 16, trailLength: 20, density: 0.6, mode: 'overlay', time: 0 } },
  glitch: { enabled: false, config: { rgbShift: 8, blockShift: 15, blockCount: 4, speed: 1.5, direction: 'right', time: 0 } },
  crt: { enabled: false, config: { scanlineStrength: 0.5, scanlineSpacing: 2, curvature: 0.25, edgeRgbShift: 4, crtVignette: 0.4, time: 0 } },
  order: ['vignette', 'noise', 'waves', 'intervals', 'glitch', 'crt', 'matrix', 'beam']
}

// ============================================
// 参数范围定义
// ============================================

/** Vignette 参数范围 */
export const VIGNETTE_RANGES = {
  strength: { min: 0, max: 1, step: 0.01 },
  radius: { min: 0.3, max: 1, step: 0.01 },
  softness: { min: 0.1, max: 0.5, step: 0.01 }
}

/** Noise 参数范围 */
export const NOISE_RANGES = {
  strength: { min: 0, max: 1, step: 0.01 },
  scale: { min: 1, max: 10, step: 1 },
  density: { min: 0.1, max: 1, step: 0.01 },
  colored: { min: false, max: true }
}

/** Beam 参数范围 */
export const BEAM_RANGES = {
  strength: { min: 0, max: 1, step: 0.01 },
  width: { min: 10, max: 100, step: 1 },
  speed: { min: 0.1, max: 10, step: 0.1 }
}

/** Intervals 参数范围 */
export const INTERVALS_RANGES = {
  spacing1: { min: 5, max: 50, step: 1 },
  width1: { min: 1, max: 10, step: 1 },
  spacing2: { min: 5, max: 50, step: 1 },
  width2: { min: 1, max: 10, step: 1 },
  animated: { min: false, max: true }
}

/** Waves 参数范围 */
export const WAVES_RANGES = {
  strength: { min: 0, max: 1, step: 0.01 },
  frequency: { min: 0.01, max: 0.2, step: 0.01 },
  speed: { min: 0.1, max: 5, step: 0.1 },
  hueShift: { min: 0, max: 360, step: 1 },
  waveCount: { min: 2, max: 5, step: 1 }
}

/** Matrix 参数范围 */
export const MATRIX_RANGES = {
  strength: { min: 0, max: 1, step: 0.01 },
  speed: { min: 0.5, max: 5, step: 0.1 },
  charSize: { min: 10, max: 30, step: 1 },
  trailLength: { min: 5, max: 30, step: 1 },
  density: { min: 0.1, max: 1, step: 0.01 }
}

/** Glitch 参数范围 */
export const GLITCH_RANGES = {
  rgbShift: { min: 0, max: 20, step: 1 },
  blockShift: { min: 0, max: 50, step: 1 },
  blockCount: { min: 0, max: 10, step: 1 },
  speed: { min: 0.1, max: 5, step: 0.1 }
}

/** CRT 参数范围 */
export const CRT_RANGES = {
  scanlineStrength: { min: 0, max: 1, step: 0.01 },
  scanlineSpacing: { min: 2, max: 4, step: 1 },
  curvature: { min: 0, max: 0.5, step: 0.01 },
  edgeRgbShift: { min: 0, max: 10, step: 1 },
  crtVignette: { min: 0, max: 1, step: 0.01 }
}